import type { PaginatedItems } from '@api/hooks/common.ts';
import { useBankProfile } from '@contexts/BankProfileContext.tsx';
import { useAuth } from '@hooks/useAuth.ts';
import { useFF } from '@hooks/useFF.ts';
import apiClient from '@services/apiClient.ts';
import { useQuery } from '@tanstack/react-query';
import { CC_FEATURE_FLAGS } from 'types/layout.ts';
import { AccountStatus } from 'types/operations';

import type { Account } from './useAccounts';

interface CustomerAccountParams {
	currentPage?: number;
	limit?: number;
	status?: AccountStatus[];
	isFull?: boolean;
}

interface FetchAccountsParams {
	profileId: string | undefined;
	currentPage?: number;
	limit: number;
	offset?: number;
	statusParam: Record<string, string | undefined>;
}

const mapBalances = (acc: Account): Account => ({
	...acc,
	availableBalance: acc.availableBalance / 100,
	reservedAmount: acc.reservedAmount / 100,
	balance: acc.balance / 100,
});

const fetchAccounts = async ({
	profileId,
	offset,
	limit,
	statusParam,
}: FetchAccountsParams): Promise<PaginatedItems<Account[]>> => {
	const response = await apiClient.get<PaginatedItems<Account[]>>(
		`/profiles/${profileId}/accounts`,
		{ params: { offset, limit, ...statusParam } },
	);
	return { ...response.data, items: response.data.items.map(mapBalances) };
};

const fetchAllAccounts = async ({
	profileId,
	currentPage,
	limit,
	statusParam,
}: FetchAccountsParams): Promise<PaginatedItems<Account[]>> => {
	const response = await apiClient.get<PaginatedItems<Account[]>>(
		`/profiles/${profileId}/accounts/own-transfer-destinations`,
		{ params: { page: currentPage, per_page: limit, ...statusParam } },
	);

	return { ...response.data, items: response.data.items.map(mapBalances) };
};

export const useCustomerAccount = ({
	currentPage = 1,
	limit = 20,
	status = [AccountStatus.OPENED, AccountStatus.CREDIT_BLOCKED],
	isFull = false,
}: CustomerAccountParams) => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();
	const offset = currentPage === 1 ? 0 : (currentPage - 1) * limit;
	const shouldUseOwnAccounts = useFF(CC_FEATURE_FLAGS.WITH_RESTRICTED_IBANS);

	const statusParam =
		status.length > 0 ? { status: status.map((s) => s.toUpperCase()).join(',') } : {};

	return useQuery({
		queryKey: [
			'customerAccounts',
			selectedProfile?.id,
			currentPage,
			limit,
			status,
			isFull,
			shouldUseOwnAccounts,
		],
		queryFn:
			isFull && shouldUseOwnAccounts
				? () =>
						fetchAllAccounts({
							profileId: selectedProfile?.id,
							currentPage,
							limit,
							statusParam,
						})
				: () =>
						fetchAccounts({
							profileId: selectedProfile?.id,
							offset,
							limit,
							statusParam,
						}),
		enabled: !!session?.tokenized && !!selectedProfile,
	});
};
